import { RefCallback, useCallback, useRef } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import { useImmer } from 'use-immer';

type ElementSize = { offsetWidth: number; offsetHeight: number };

/**
 * Helper hook to listen to and get the dimensions of an element
 * This hook returns
 * - a callback that should be passed to an element's ref prop, and
 * - the size of the element
 */
export const useElementSize = (): [RefCallback<HTMLElement>, ElementSize] => {
  const ref = useRef<HTMLElement | null>(null);

  const [size, setSize] = useImmer({ offsetWidth: 0, offsetHeight: 0 });

  const refCallback = useCallback(
    (elem: HTMLElement | null) => {
      ref.current = elem;
      setSize((draft) => {
        draft.offsetWidth = elem?.offsetWidth ?? 0;
        draft.offsetHeight = elem?.offsetHeight ?? 0;
      });
    },
    [setSize]
  );

  useResizeObserver(ref, () => refCallback(ref.current));

  return [refCallback, size];
};
